<script lang="ts">
	import { afterUpdate, createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { location, push, replace } from 'svelte-spa-router';

	//Helpers
	import {
		addDashboard,
		deleteDashboard,
		onEndDrag,
		onStartDrag,
		checkIsEventLogTab,
		requestReorderDashboards,
		requestChangeDashboardName,
		checkIsCameraMonitoringTab,
		getIsActivityLogVisible,
		getIsCameraMonitoringVisible,
	} from '../list-helpers';
	import { save_home } from '../../../helpers/home_helpers';
	// Stores
	import { dashboardTabs } from 'src/stores/features/dashboards';
	import { home_data, user_data, user_settings } from 'src/stores/account/user';
import { devices } from 'src/stores/lists/devices';
	import { hasCameraDevices } from 'src/components/Camera/helpers/camera-helpers';

	import {
		Button,
		Modal,
		Text,
		Container,
		Input,
		IconButton,
		CardGrid,
		ListNumberCard,
		Alert,
		Toggle,
		Icon,
	} from '@Shelly/shelly-design-system';

	import type { TypeTab } from '../../../types/Tabs';
	import { NON_PREMIUM_DASH_TABS_LIMIT, PREMIUM_DASH_TABS_LIMIT } from 'src/constants';
	import { capFirstChar } from 'src/helpers/utilities';
	const isPremium = $user_data.premium || $user_settings.been_premium;

	const DASHBOARDS_LIMIT = isPremium ? PREMIUM_DASH_TABS_LIMIT : NON_PREMIUM_DASH_TABS_LIMIT;

	const alertMessegaLimitTranslation = isPremium
		? 'alert_premium_dashboard_limit_reached'
		: 'alert_basic_dashboard_limit_reached';

	const alertMessageReachedLimitDefault = isPremium
		? `Dashboard limit of ${PREMIUM_DASH_TABS_LIMIT} reached! Remove some of your dashboards to add a new one.`
		: `Dashboard limit of ${NON_PREMIUM_DASH_TABS_LIMIT} reached! Upgrade to Premium to create up to ${PREMIUM_DASH_TABS_LIMIT} dashboards.`;

	const flipDurationMs = 0;
	let newTabName = '';
	let dragDisabled = true;
	let showDeleteModal = false;
	let tabToDelete = null;
	let newCardAdded = false;
	let lastInputRef: HTMLElement;
	let dashTabItem = [...$dashboardTabs].filter(checkIsEventLogTab)[0];

	const dispatch = createEventDispatcher();
	export let dashboardsEdit = false;

	$: cameraMonitoringTabItem = [...$dashboardTabs].filter(checkIsCameraMonitoringTab)[0];
	$: isCameraUser = hasCameraDevices($devices);
	$: disableAdd = newTabName.trim() === '';
	$: isActivityLogVisible = getIsActivityLogVisible($home_data);
	$: isCameraMonitoringVisible = getIsCameraMonitoringVisible($home_data);
	$: dashCardNumberOffset = isCameraUser ? 3 : 2;
	interface DashboardTab extends TypeTab {
		isDndShadowItem?: boolean;
	}

	let items: DashboardTab[];
	$: items = [...$dashboardTabs].filter(
		(tab) => !checkIsEventLogTab(tab) && !checkIsCameraMonitoringTab(tab),
	);

	let dndSettings: any;
	$: dndSettings = {
		items,
		flipDurationMs: flipDurationMs,
		dragDisabled: dragDisabled,
		type: 'dashListRender',
		transformDraggedElement,
	};
	$: listNewValues = items.reduce((acc, curr) => ((acc[curr.id] = curr.text), acc), {});

	afterUpdate(() => {
		focusInputOnAddNewTab();
	});

	const onCloseDeleteModal = () => {
		tabToDelete = -1;
		showDeleteModal = false;
	};

	const onCloseModal = () => {
		dashboardsEdit = false;
		listNewValues = items.reduce((acc, curr) => ((acc[curr.id] = curr.text), acc), {});
		newTabName = '';
		dispatch('close');
	};

	const onAddNewTab = async () => {
		if (newTabName) {
			const id = await addDashboard(newTabName.trim());
			console.log('id', id);
			newTabName = '';
			newCardAdded = true;

			push(`/dash/${id}`);
		}
	};

	const handleDeleteDashboard = (tab) => {
		tabToDelete = tab;
		showDeleteModal = true;
	};

	const onDeleteCard = async () => {
		if ($location.includes('/dash/' + tabToDelete.id)) {
			replace('/dash/-1');
		}
		await deleteDashboard(tabToDelete.id);
		tabToDelete = null;
		showDeleteModal = false;
	};

	const onChangeDashboardName = async (id, newName) => {
		await requestChangeDashboardName(id, newName);
	};

	const updateActivityLogVisible = () => {
		const newValue = !isActivityLogVisible;

		if ($location === '/dash/event_log') {
			replace('/dash/-1');
		}

		save_home('isActivityLogVisible', { value: newValue });
		home_data.set({
			...$home_data,
			isActivityLogVisible: { value: newValue },
		});
	};

	const updateCameraMonitoringVisible = () => {
		const newValue = !isCameraMonitoringVisible;

		if ($location === '/dash/camera_monitoring') {
			replace('/dash/-1');
		}

		save_home('isCameraMonitoringVisible', { value: newValue });
		home_data.set({
			...$home_data,
			isCameraMonitoringVisible: { value: newValue },
		});
	};

	// DND Functions
	const handleStartDrag = () => {
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		dragDisabled = true;
	};

	const handleReorder = (e: CustomEvent) => {
		items = e.detail.items;
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		let editedList = {
			[`${dashTabItem.id}`]: {
				...dashTabItem,
			},
		};
		e.detail.items.map((v, i) => {
			delete v?.isDndShadowItem;

			editedList[v.id] = {
				...v,
				position: i,
			};
		});

		if (Object.keys(editedList).length === 0) return;

		items = [...e.detail.items];
		handleEndDrag();

		await requestReorderDashboards(editedList);
	};

	const transformDraggedElement = (draggedEl: HTMLElement) => {
		const child = draggedEl.firstChild as HTMLElement;
		// Focus dragged element and hide number
		child.setAttribute('tabIndex', '-1');
		child.classList.add('on-drag');
		child.focus();
	};

	const focusInputOnAddNewTab = () => {
		if (newCardAdded && lastInputRef) {
			// Scroll to input when added new card
			lastInputRef.focus();
			newCardAdded = false;
			lastInputRef.blur();
		}
	};

	const onItemNameInput = (event, tab) => {
		const target = event.target as HTMLInputElement;
		listNewValues[tab.id] = target.value;
	};

	const onNewTabNameInput = (event) => {
		const target = event.target as HTMLInputElement;
		newTabName = target.value;
	};

	const plan = isPremium ? $format('label_premium') : $format('label_basic_plan');
</script>

<Modal
	on:close={onCloseModal}
	showModal={dashboardsEdit}
	title={$format('modal_title_edit_dashboards', { default: 'Edit Dashboards' })}
	contentDirection="row"
>
	<svelte:fragment slot="toolbar">
		<!-- values:{basicPlanLimit:NON_PREMIUM_DASH_TABS_LIMIT, premiumPlanLimit:},  -->
		{#if items.length >= DASHBOARDS_LIMIT}
			<Alert
				customIcon={!isPremium ? 'fas fa-gem icon-premium-color' : ''}
				type="warning"
				heading={$format(alertMessegaLimitTranslation, {
					values: {
						limit: DASHBOARDS_LIMIT,
						premiumLimit: PREMIUM_DASH_TABS_LIMIT,
					},
					default: alertMessageReachedLimitDefault,
				})}
				class="mb-l"
				hideSlotBottom={isPremium}
			>
				<Button
					slot="bottom"
					variant="premium"
					size="m"
					width="auto"
					on:click={() => {
						onCloseModal();
						push('/settings/premium');
					}}
				>
					{capFirstChar(
						$format('button_get_premium', {
							default: 'Get Premium',
						}),
					)}
				</Button>
			</Alert>
		{:else}
			<Alert
				class="mb-l"
				heading={$format('alert_heading_dashboard_created', {
					values: { limit: DASHBOARDS_LIMIT, count: items.length },
					default: `Dashboards created: ${items.length}/${DASHBOARDS_LIMIT}`,
				})}
				message={$format('alert_message_dashboard_created', {
					values: {
						limit: DASHBOARDS_LIMIT,
						plan: plan,
					},
					default: `${plan} allows to create up to ${DASHBOARDS_LIMIT} dashboards.`,
				})}
				isHeadingBold
			/>
		{/if}

		<Container noScroll padding="0">
			<Text class="mb-s text-capitalize" element="h2">
				{$format('text_sub_title_add_dashboard', { default: 'add dashboard' })}
			</Text>

			<div class="d-flex">
				<Input
					class="mr-s"
					label={$format('input_dashboard_modal_tab_name_placeholder', {
						default: 'Tab Name',
					})}
					icon={null}
					value={newTabName}
					disabled={items.length >= DASHBOARDS_LIMIT}
					on:input={items.length <= DASHBOARDS_LIMIT && onNewTabNameInput}
					id="dashboard-tab-name-input"
					aria-labelledby="dashboard-tab-name-input"
				/>
				<Button
					disabled={items.length >= DASHBOARDS_LIMIT && disableAdd}
					size="m"
					on:click={items.length <= DASHBOARDS_LIMIT && onAddNewTab}
				>
					{$format('process_add')}
				</Button>
			</div>
		</Container>
		<hr class="mt-l mb-md-0 mb-sm-s" />
	</svelte:fragment>

	<svelte:fragment slot="content">
		<!-- Static tabs -->
		<Container padding="0">
			<Text class="mb-m text-capitalize" element="h2">
				{$format('text_sub_title_current_dashboards', {
					default: 'current dashboards',
				})}
			</Text>
			<CardGrid sm={100} widthUnit="%">
				<div>
					<ListNumberCard cardNumber="01" editMode>
						<Toggle
							id={`dashboard-activity-log-toggle`}
							aria-labelledby={`dashboard-activity-log-toggle`}
							size="m"
							checked={isActivityLogVisible}
							on:click={updateActivityLogVisible}
						>
							<span class="text-capitalize">
								{$format(dashTabItem?.text, {
									default: 'Activity Log',
								})}
							</span>
						</Toggle>
					</ListNumberCard>
				</div>
			</CardGrid>

			{#if isCameraUser}
				<CardGrid sm={100} widthUnit="%">
					<div>
						<ListNumberCard cardNumber="02" editMode>
							<Toggle
								id={`dashboard-camera-monitoring-toggle`}
								aria-labelledby={`dashboard-camera-monitoring-toggle`}
								size="m"
								checked={isCameraMonitoringVisible}
								on:click={updateCameraMonitoringVisible}
							>
								<span class="text-capitalize">
									{$format(cameraMonitoringTabItem?.text, {
										default: 'Camera Monitoring',
									})}
								</span>
							</Toggle>
						</ListNumberCard>
					</div>
				</CardGrid>
			{/if}

			<CardGrid
				sm={100}
				widthUnit="%"
				editMode={true}
				{dndSettings}
				on:consider={handleReorder}
				on:finalize={handleReorderEnd}
				modalContent
				noPaddingBottom
			>
				{#each items as tab, index (tab)}
					<div class:dnd-placeholder={tab?.isDndShadowItem}>
						<ListNumberCard
							cardNumber={`${index + dashCardNumberOffset}`.padStart(2, '0')}
							editMode
						>
							<IconButton
								class="mr-s"
								icon="fas fa-up-down-left-right"
								size="s"
								variant="primary"
								on:mousedown={(e) => onStartDrag(e, true, handleStartDrag)}
								on:touchstart={(e) => onStartDrag(e, true, handleStartDrag)}
								on:mouseup={(e) => onEndDrag(e, true, handleEndDrag)}
								on:touchend={(e) => onEndDrag(e, true, handleEndDrag)}
								dndHandle
								aria-label={$format('aria_drag_tab', { default: 'Drag tab' })}
							/>
							{#if index === items.length - 1}
								<Input
									id={`dashboard-tab-name-input-${index}`}
									aria-labelledby={$format('aria_dashboard_tab_name_input', {
										default: 'Dashboard tab name input',
									})}
									bind:ref={lastInputRef}
									value={listNewValues[tab.id]}
									icon={null}
									on:input={(e) => onItemNameInput(e, tab)}
								/>
							{:else}
								<Input
									id={`dashboard-tab-name-input-${index}`}
									aria-labelledby={$format('aria_dashboard_tab_name_input', {
										default: 'Dashboard tab name input',
									})}
									value={listNewValues[tab.id]}
									icon={null}
									on:input={(e) => onItemNameInput(e, tab)}
								/>
							{/if}
							<IconButton
								aria-label={$format('aria_save_tab', { default: 'Save tab' })}
								class="ml-s"
								icon="fas fa-save"
								variant="primary"
								disabled={listNewValues[tab.id] === tab.text}
								on:click={() => {
									onChangeDashboardName(tab.id, listNewValues[tab.id]);
								}}
							/>
							<IconButton
								aria-label={$format('aria_delete_tab', {
									default: 'Delete tab',
								})}
								disabled={tab.id === -1}
								class="ml-s"
								icon="fas fa-trash"
								variant="danger"
								on:click={() => {
									handleDeleteDashboard(tab);
								}}
							/>
						</ListNumberCard>
					</div>
				{/each}
			</CardGrid>
		</Container>
	</svelte:fragment>
</Modal>
<Modal
	on:close={onCloseDeleteModal}
	showModal={showDeleteModal}
	title={$format('prompt_title_delete_dashboard', {
		default: 'Delete Dashboard?',
	})}
>
	<Container slot="content" padding="0">
		<div class="d-flex flex-direction-column align-items-center">
			<Icon size="xxl" icon="fa-light fa-trash" class="text-danger mb-xs" />
			<Text class="text-bold text-center mb-s" styleType="controls-large">
				{tabToDelete.text}
			</Text>
			<Text class="width-full">
				{$format('on_delete_item_msg', {
					values: {
						device_name: tabToDelete.text,
						type: $format('label_menu_items_dashboard', {
							default: 'Dashboard',
						}),
					},
					default: `Deleting ${$format('label_menu_items_dashboard', {
						default: 'Dashboard',
					}).toLowerCase()} ‘${tabToDelete.text}’ will permanently remove it from your account.`,
				})}
			</Text>
		</div>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={onCloseDeleteModal}
			aria-label={$format('aria_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="danger"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onDeleteCard}
			aria-label={$format('aria_delete_tab', { default: 'Delete tab' })}
		>
			{$format('button_delete_dashboard', { default: 'Delete dashboard' })}
		</Button>
	</div>
</Modal>
